// Giphy v1 search wrapper. Tenor's public API was deprecated in favour of
// Giphy (see https://developers.giphy.com/docs/api/tenor-migration). The
// API key is provided server-side via Config::SecretLoader and exposed
// to React in SharedProps.giphy_api_key — the caller passes it in.

const GIPHY_BASE = 'https://api.giphy.com/v1/gifs'

export interface GiphyGif {
  id: string
  description: string
  previewUrl: string
  gifUrl: string
  width: number
  height: number
}

interface GiphyImage {
  url: string
  width: string
  height: string
}

interface GiphyResult {
  id: string
  title?: string
  images: {
    preview_gif?: GiphyImage
    fixed_height_small?: GiphyImage
    fixed_height?: GiphyImage
    downsized?: GiphyImage
    original?: GiphyImage
  }
}

interface GiphyResponse {
  data: GiphyResult[]
}

function toGif(r: GiphyResult): GiphyGif | null {
  const preview = r.images.fixed_height_small ?? r.images.preview_gif ?? r.images.fixed_height
  const full = r.images.downsized ?? r.images.fixed_height ?? r.images.original
  if (!preview?.url || !full?.url) return null
  return {
    id: r.id,
    description: r.title ?? '',
    previewUrl: preview.url,
    gifUrl: full.url,
    width: Number(full.width) || 0,
    height: Number(full.height) || 0
  }
}

export async function searchGifs(apiKey: string, query: string, limit = 24): Promise<GiphyGif[]> {
  const params = new URLSearchParams({
    api_key: apiKey,
    q: query,
    limit: String(limit),
    rating: 'g',
    bundle: 'messaging_non_clips'
  })
  const res = await fetch(`${GIPHY_BASE}/search?${params}`)
  if (!res.ok) throw new Error(`Giphy search failed: ${res.status}`)
  const json = await res.json() as GiphyResponse
  return json.data.map(toGif).filter((g): g is GiphyGif => g !== null)
}

export async function trendingGifs(apiKey: string, limit = 24): Promise<GiphyGif[]> {
  const params = new URLSearchParams({
    api_key: apiKey,
    limit: String(limit),
    rating: 'g',
    bundle: 'messaging_non_clips'
  })
  const res = await fetch(`${GIPHY_BASE}/trending?${params}`)
  if (!res.ok) throw new Error(`Giphy trending failed: ${res.status}`)
  const json = await res.json() as GiphyResponse
  return json.data.map(toGif).filter((g): g is GiphyGif => g !== null)
}
